import { Bell, Sparkles, Flower2, Music, Sailboat, Footprints, Clock, MessageCircle } from "lucide-react";
import { Reveal } from "./Reveal";
import { waLink } from "../data";
import { useLang } from "../i18n";

const PLACES = [
  {
    icon: Bell,
    name: "Banke Bihari Ji Mandir",
    hindi: "बाँके बिहारी",
    desc: "The beating heart of Vrindavan. Darshan happens in quick jhankis as the curtain opens and closes — a tradition found nowhere else in the world.",
    tip: "Go early morning; darshan roughly 8–11 AM & 5–8 PM",
  },
  {
    icon: Sparkles,
    name: "Prem Mandir",
    hindi: "प्रेम मंदिर",
    desc: "A white-marble wonder carved with Krishna's leelas. Stay after sunset when the whole temple glows and the musical fountain begins.",
    tip: "Most magical after sunset — illumination from ~7 PM",
  },
  {
    icon: Flower2,
    name: "Nidhivan",
    hindi: "निधिवन",
    desc: "The sacred tulsi grove where Radha-Krishna's raas leela is believed to happen every night. Gates close at dusk — even the monkeys leave.",
    tip: "Visit before dusk; pair with nearby Seva Kunj",
  },
  {
    icon: Music,
    name: "ISKCON Krishna Balaram Mandir",
    hindi: "इस्कॉन",
    desc: "Marble courtyard, 24-hour kirtan and hot prasadam. The 4:30 AM mangala aarti here is an experience devotees travel continents for.",
    tip: "Mangala aarti 4:30 AM; evening aarti ~7 PM",
  },
  {
    icon: Sailboat,
    name: "Kesi Ghat & Yamuna Aarti",
    hindi: "केशी घाट",
    desc: "Lamps, chants and boats on Maa Yamuna — the evening aarti here is Vrindavan's most peaceful hour, on the ghat where Krishna slew Kesi.",
    tip: "Reach by 6 PM; take a sunset boat ride",
  },
  {
    icon: Footprints,
    name: "Radha Raman & Old Town Lanes",
    hindi: "राधा रमण",
    desc: "A self-manifested deity from 1542, plus Shahji, Radha Vallabh and Govind Dev Ji hidden in the old lanes — Vrindavan as it has always been.",
    tip: "Best explored on foot with a local guide",
  },
];

const DAYS = [
  {
    day: "Day 1",
    title: "Old Vrindavan",
    steps: [
      ["4:30 AM", "Mangala aarti at ISKCON (optional, worth it)"],
      ["7:30 AM", "Banke Bihari Ji darshan — beat the rush"],
      ["9:00 AM", "Bedmi kachori breakfast in the old lanes"],
      ["10:00 AM", "Nidhivan, Seva Kunj & Radha Raman walk"],
      ["1:00 PM", "Lunch & rest at Shri Jamuna Dham"],
      ["5:30 PM", "Kesi Ghat — Yamuna aarti & boat ride"],
      ["8:00 PM", "Pure-veg dinner served in your room"],
    ],
  },
  {
    day: "Day 2",
    title: "New Vrindavan & Beyond",
    steps: [
      ["9:00 AM", "Prem Mandir — morning darshan & gardens"],
      ["11:00 AM", "ISKCON daytime darshan & prasadam"],
      ["1:00 PM", "Lunch & rest at the dham"],
      ["6:30 PM", "Prem Mandir illumination & fountain show"],
      ["Day 3+", "Mathura Janmabhoomi, Govardhan parikrama or Barsana — we arrange autos & taxis"],
    ],
  },
];

export const Explore = () => {
  const { s } = useLang();
  const places = PLACES.map((p, i) => ({ ...p, ...s.explore.places[i] }));
  const days = DAYS.map((d, i) => ({ ...d, ...s.explore.itin.days[i] }));
  return (
  <section id="explore" className="relative bg-cream py-24 sm:py-32 overflow-hidden" data-testid="explore-section">
    <span
      aria-hidden="true"
      className="pointer-events-none select-none absolute -right-8 top-8 font-hindi text-[16vw] leading-none text-maroon/[0.04]"
    >
      ब्रज
    </span>
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.25em] text-peacock font-semibold" data-testid="explore-overline">
          {s.explore.overline}
        </p>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-maroon tracking-tight max-w-2xl" data-testid="explore-heading">
            {s.explore.heading}
          </h2>
          <p className="max-w-sm text-sm leading-relaxed text-stone2">
            {s.explore.note}
          </p>
        </div>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="places-grid">
        {places.map((p, i) => (
          <Reveal key={p.name} delay={i * 0.06}>
            <article
              className="group flex h-full flex-col rounded-2xl border border-maroon/10 bg-white p-7 shadow-sm shadow-maroon/5 transition-colors duration-300 hover:border-saffron/50"
              data-testid={`place-card-${p.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
            >
              <div className="flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-maroon/5 text-maroon transition-colors duration-300 group-hover:bg-saffron/15 group-hover:text-saffron">
                  <p.icon size={22} />
                </span>
                <span className="font-hindi text-xl text-saffron/80">{p.hindi}</span>
              </div>
              <h3 className="mt-5 font-display text-xl font-bold text-ink tracking-tight">{p.name}</h3>
              <p className="mt-2.5 flex-1 text-sm leading-relaxed text-stone2">{p.desc}</p>
              <p className="mt-4 flex items-start gap-2 text-xs font-medium text-peacock">
                <Clock size={13} className="mt-0.5 shrink-0" /> {p.tip}
              </p>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-16 rounded-3xl bg-maroon p-8 sm:p-12 grain relative overflow-hidden" data-testid="itinerary-panel">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-gold font-semibold">
                {s.explore.itin.overline}
              </p>
              <h3 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-cream tracking-tight">
                {s.explore.itin.heading}
              </h3>
            </div>
            <a
              href={waLink("Namaste! We're planning our Vrindavan trip. Please help us plan darshan timings and local travel.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="itinerary-whatsapp-button"
              className="inline-flex items-center gap-2 rounded-full bg-saffron px-6 py-3 text-sm font-semibold text-night transition-colors duration-300 hover:bg-gold"
            >
              <MessageCircle size={16} /> {s.explore.itin.cta}
            </a>
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
            {days.map((d) => (
              <div key={d.day} data-testid={`itinerary-${d.day.toLowerCase().replace(/\s/g, "-")}`}>
                <p className="font-display text-lg font-bold text-saffron">
                  {d.day} <span className="text-cream/60 font-normal">— {d.title}</span>
                </p>
                <ol className="mt-5 space-y-0">
                  {d.steps.map(([time, activity], i) => (
                    <li key={i} className="relative flex gap-4 pb-5 last:pb-0">
                      {i < d.steps.length - 1 && (
                        <span className="absolute left-[5px] top-4 h-full w-px bg-cream/15" />
                      )}
                      <span className="mt-1.5 h-[11px] w-[11px] shrink-0 rounded-full border-2 border-gold bg-maroon" />
                      <div>
                        <p className="text-xs font-bold tracking-wider text-gold">{time}</p>
                        <p className="mt-0.5 text-sm leading-relaxed text-cream/80">{activity}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
